// NC1 read path (AWS-PORT chunk 4; v2 per EPOCH-KEYPAIR.md): assemble a
// verified account context from the key layer and decrypt NC1 records. The
// Virgil twin of this logic lives in E2EEHelper (E3Kit); the dispatcher there
// routes by sniffing the NC1 magic, so both schemes coexist per record
// (dual-read viewer).
//
// Port of the dev server's shared/session.js openAccountContext with ONE
// substitution: "who am I" comes from the Keystore's cached unwrapped
// identity (passkey / link / recovery unlock) instead of a password unwrap.
// Every §12 transparency check is kept:
//   - verify the hash chain BEFORE trusting any key document,
//   - owner identity key must match chain custody,
//   - member list + owner device list must match their chain commitments,
//   - provenance trust root = the OWNER's signed device list only (a
//     view-only guest holds the epoch secrets and could forge ciphertext,
//     but their devices are never valid record authors),
//   - pin the verified head so rollbacks/forks are detected across sessions.
//
// v2: the ADK rows hold each epoch's X25519 SECRET (sealed to my identity
// key); every envelope carries its own random record key sealed to the
// epoch PUBLIC key, which openParsed opens with that secret. The public key
// the member list publishes must derive from the secret I hold — a mismatch
// means the server (or a stale owner) is advertising a key nobody can open.

import E2eeApi from '@/aws/e2ee/E2eeApi'
import store from '@/store'
import { getSodium } from './sodium'
import { loadIdentity, clearIdentity } from './Keystore'
import { Nc1UnlockRequiredError, Nc1AccessNotGrantedError } from '../E2EEErrors'
import {
  b64, ub64, parseEnvelope, parseEnvelopeString, openParsed, epochPkFromSk,
  deviceListSignedBytes, memberListSignedBytes,
} from './shared/envelope.js'
import {
  verifyChain, assertDocCommitted,
  docHashIdentity, docHashDeviceList, docHashMemberList,
} from './shared/translog.js'

const NC1_MAGIC = [0x4e, 0x43, 0x31] // "NC1"

/** Does this Uint8Array carry an NC1 envelope? */
export function isNc1Bytes (bytes) {
  return bytes && bytes.length > 4 &&
    bytes[0] === NC1_MAGIC[0] && bytes[1] === NC1_MAGIC[1] && bytes[2] === NC1_MAGIC[2]
}

/** Does this base64 string decode to an NC1 envelope? (cheap prefix check) */
export function isNc1String (str) {
  if (typeof str !== 'string' || str.length < 8) return false
  // "NC1" base64-encodes to "TkMx..." — no decode needed to route.
  return str.startsWith('TkMx')
}

// Pinned transparency head, persisted per crypto accountId — the browser
// twin of the Windows keystore's TranslogHead. THE ONLY head store the
// webapp uses (the vendored translog.js defaultHeadStore is not used here).
const headStore = {
  key: (accountId) => `nc1.translogHead.${accountId}`,
  load (accountId) {
    try { return JSON.parse(localStorage.getItem(this.key(accountId))) } catch { return null }
  },
  save (accountId, head) {
    try { localStorage.setItem(this.key(accountId), JSON.stringify(head)) } catch { /* private mode */ }
  },
  forget (accountId) {
    try { localStorage.removeItem(this.key(accountId)) } catch { /* storage unavailable */ }
  },
}

/**
 * Drop the pinned head for an account whose chain was legitimately replaced
 * (finding ⑯ viewer-only re-genesis). Without this the next context build
 * trips the rollback alarm against a chain that no longer exists.
 */
export function forgetPinnedHead (accountId) {
  headStore.forget(accountId)
}

// One verified context per account email, built at most once per page load
// (invalidated on unlock and on unknown-device retries so a freshly linked
// device's records become readable without a refresh).
const contextCache = new Map()

export function invalidateContext (email) {
  if (email) contextCache.delete(String(email).toLowerCase())
  else contextCache.clear()
}

/**
 * Build (or reuse) the verified account context:
 *   { sodium, devicePks: Map<deviceId,Uint8Array>, revokedDevices: Set,
 *     epochSks: Map<epoch,Uint8Array> }  (+ openParsed's lazy epochPks/recordKeys caches)
 * Throws Nc1UnlockRequiredError when this browser's keystore has no identity
 * for the account — the UI routes that to the unlock dialog.
 */
export function getContext (accountEmail) {
  const email = String(accountEmail).toLowerCase()
  if (!contextCache.has(email)) {
    const p = buildContext(email)
    // A failed build must not poison the cache (e.g. transient network).
    p.catch(() => contextCache.delete(email))
    contextCache.set(email, p)
  }
  return contextCache.get(email)
}

async function buildContext (email) {
  // PERF (2026-08-14): everything fetchable starts in ONE parallel wave —
  // the four API reads, the IndexedDB identity load, and the libsodium wasm
  // init. Fetch ORDER carries no trust; the §12 VERIFICATION order below is
  // unchanged and still gates everything before anything is believed. The
  // device list is prefetched by the account email — which IS the owner's
  // email by construction (accounts are keyed by their owner); the rare
  // mismatch is refetched below.
  const myEmail = String(store.state.account?.accountId || '').toLowerCase()
  const sodiumP = getSodium()
  const identityP = loadIdentity(email)
  const myIdentityP = (myEmail && myEmail !== email) ? loadIdentity(myEmail) : null
  const memberListP = E2eeApi.getMemberList(email)
  const logP = E2eeApi.getLog(email).then((r) => r.data || []).catch(() => [] /* legacy */)
  const deviceListP = E2eeApi.getDeviceList(email).then((r) => r.data).catch(() => null)
  const adkP = E2eeApi.getAdkEnvelopes(email).then((r) => r.data || []).catch(() => [])
  // If an early check throws (e.g. unlock required), the in-flight fetches
  // must not surface as unhandled rejections.
  ;[memberListP, logP, deviceListP, adkP].forEach((p) => p.catch(() => {}))

  const sodium = await sodiumP

  // Whose identity opens this account (§8.5, chunk 5 — guest-aware):
  //   1. The VIEWED account's identity cached in this browser — the owner on
  //      their own account, or a browser the owner viewer-provisioned via
  //      device-transfer (chunk 2's low-friction partner onboarding).
  //   2. Else the LOGGED-IN user's own identity — a §8.5 member/guest whose
  //      ADK rows are addressed to their own userId.
  // The context below is identity-agnostic: provenance is always verified
  // against the VIEWED account's owner, and epoch secrets are filtered by
  // whichever userId the loaded identity carries.
  let cached = await identityP
  let cachedFrom = email
  if ((!cached || !cached.secrets) && myIdentityP) {
    cached = await myIdentityP
    cachedFrom = myEmail
  }
  if (!cached || !cached.secrets) {
    // Guests unlock THEIR OWN identity (their passkey/recovery); owners
    // unlock the account's.
    throw new Nc1UnlockRequiredError(myEmail && myEmail !== email ? myEmail : email)
  }
  const secrets = cached.secrets
  const myUserId = cached.altAccountId

  const memberList = (await memberListP).data
  const owner = memberList.members.find((m) => m.role === 'owner')
  if (!owner) throw new Error('Account member list has no owner.')

  // §12: verify the chain before trusting any document. Legacy accounts
  // without a log fall back to TOFU on the owner key (loud warning).
  const translog = await logP
  let chain = null
  if (translog.length) {
    chain = verifyChain(sodium, memberList.accountId, translog, headStore.load(memberList.accountId))
    if (owner.idSignPk !== chain.head.signerPk) {
      throw new Error('Owner identity key does not match transparency-log custody (possible key substitution).')
    }
    if (chain.latest.identity.docHash !== docHashIdentity(sodium, {
      idEncPk: owner.idEncPk, idSignPk: owner.idSignPk, identityVersion: chain.latest.identity.docVersion,
    })) {
      throw new Error('Owner identity keys do not match the transparency-log commitment.')
    }
    assertDocCommitted('member-list', chain.latest.memberList,
      memberList.version, docHashMemberList(sodium, memberList))
  } else {
    console.warn(`[nc1] no transparency log for ${email} — legacy TOFU on the owner key`)
  }

  const ownerSigOk = sodium.crypto_sign_verify_detached(
    ub64(sodium, memberList.ownerSig), memberListSignedBytes(memberList), ub64(sodium, owner.idSignPk))
  if (!ownerSigOk) throw new Error('Account member list signature is not authentic.')

  // Stale-identity detection (post-rotation, chunk 7): if the cached identity
  // CLAIMS to be this account's owner identity but its keys no longer match
  // the (chain-verified) published owner keys, the account rotated since this
  // browser cached its copy. The stale secrets open nothing new — clear them
  // so this browser reads as locked, and route to the unlock dialog (new
  // recovery code / link from an up-to-date surface). Guests are exempt:
  // their identity is a different user's and never matches owner custody.
  if (cached.altAccountId === memberList.accountId && secrets.id_sign_pk !== owner.idSignPk) {
    await clearIdentity(cachedFrom)
    throw new Nc1UnlockRequiredError(cachedFrom)
  }

  // Provenance trust root: the OWNER's devices only.
  const devicePks = new Map()
  const revokedDevices = new Set()
  let ownerDl = await deviceListP
  if (owner.email && String(owner.email).toLowerCase() !== email) {
    // Theoretical only (account email ≠ owner email) — refetch by the
    // member list's authoritative owner email.
    try { ownerDl = (await E2eeApi.getDeviceList(owner.email)).data } catch (e) { ownerDl = null }
  }
  if (chain) {
    if (!ownerDl) throw new Error('Transparency log commits an owner device list but the server returned none.')
    assertDocCommitted('device-list', chain.latest.deviceList,
      ownerDl.version, docHashDeviceList(sodium, ownerDl))
  }
  if (ownerDl) {
    if (!sodium.crypto_sign_verify_detached(
      ub64(sodium, ownerDl.identitySig), deviceListSignedBytes(ownerDl), ub64(sodium, owner.idSignPk))) {
      throw new Error('Owner device list is not authentic under the owner identity key.')
    }
    for (const d of ownerDl.devices) {
      devicePks.set(d.deviceId, ub64(sodium, d.devSignPk))
      if (d.revokedAt != null) revokedDevices.add(d.deviceId)
    }
  }

  // My epoch secrets (one ADK row per epoch, sealed to my identity-encryption key).
  const adkEnvelopes = await adkP
  const epochSks = new Map()
  let sealedToAnotherKey = 0
  for (const env of adkEnvelopes) {
    if (env.userId !== myUserId) continue
    try {
      epochSks.set(env.keyEpoch, sodium.crypto_box_seal_open(
        ub64(sodium, env.sealedAdk), ub64(sodium, secrets.id_enc_pk), ub64(sodium, secrets.id_enc_sk)))
    } catch (e) {
      // Addressed to my userId but sealed to a key I no longer hold: the
      // owner's member-list snapshot predates my identity reset/rotation
      // (finding ⑯). For a guest that is "not granted to THIS identity yet"
      // — the owner must re-verify and re-grant — not a generic decrypt bug.
      if (cachedFrom !== email) { sealedToAnotherKey += 1; continue }
      throw e
    }
  }

  // A viewer whose OWN identity opened this context but who holds no epoch
  // secret for the viewed account has simply not been granted encrypted
  // access (invite and grant are separate steps). Say so now, rather than
  // letting every record fail and surface as a generic decrypt error telling
  // them to email support (finding ⑤c, §5.2).
  if (epochSks.size === 0 && cachedFrom !== email) {
    throw new Nc1AccessNotGrantedError(email, cachedFrom, { staleGrant: sealedToAnotherKey > 0 })
  }

  // v2 integrity: the public key the (chain-committed, owner-signed) member
  // list advertises for the CURRENT epoch must be the one my sealed secret
  // derives — otherwise uploaders are sealing to a key nobody here can open.
  const currentSk = epochSks.get(memberList.keyEpoch)
  if (currentSk && memberList.epochPk && b64(sodium, epochPkFromSk(sodium, currentSk)) !== memberList.epochPk) {
    throw new Error(`Published epoch key for epoch ${memberList.keyEpoch} does not match the sealed epoch secret (possible key substitution).`)
  }

  if (chain) headStore.save(memberList.accountId, chain.head)

  return { sodium, devicePks, revokedDevices, epochSks }
}

/**
 * Open one parsed envelope against the account context, refreshing the
 * context ONCE when the author device is unknown or the epoch is newer than
 * the cached context (a device linked / an epoch bumped after the context
 * was cached — the NC1 twin of Virgil's stale-card retry).
 */
async function openWithRetry (email, parsed) {
  let ctx = await getContext(email)
  let r = openParsed(ctx.sodium, parsed, ctx)
  if (!r.ok && /unknown device|no epoch key/.test(r.error)) {
    invalidateContext(email)
    ctx = await getContext(email)
    r = openParsed(ctx.sodium, parsed, ctx)
  }
  if (!r.ok) throw new Error(`NC1 decrypt failed: ${r.error}`)
  if (r.revoked) console.warn('[nc1] record author device is revoked (historical record)')
  return r.plaintext
}

/** Decrypt an NC1 image blob → Blob typed image/jpeg. */
export async function decryptImageBlob (accountEmail, bytes) {
  const sodium = await getSodium()
  const parsed = parseEnvelope(sodium, bytes)
  const plaintext = await openWithRetry(String(accountEmail).toLowerCase(), parsed)
  return new Blob([plaintext], { type: 'image/jpeg' })
}

/** Decrypt an NC1 base64 string envelope → string. */
export async function decryptStringEnvelope (accountEmail, envelopeB64) {
  const sodium = await getSodium()
  const parsed = parseEnvelopeString(sodium, envelopeB64)
  const plaintext = await openWithRetry(String(accountEmail).toLowerCase(), parsed)
  return new TextDecoder().decode(plaintext)
}
